// ./src/hooks/useDiscountCodes.ts
//
// Loads an event's checkout discount codes (active + disabled, so the management
// page can re-enable disabled ones). `reload` refetches after a change.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { DiscountCodeService, DiscountCode } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseDiscountCodesReturn {
  codes: DiscountCode[];
  loading: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

export const useDiscountCodes = (eventId?: string | null): UseDiscountCodesReturn => {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setCodes([]);
      setIsLoaded(true);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await DiscountCodeService.list(eventId, [STATUSES.ACTIVE, STATUSES.DISABLED]);

    if (myCall !== callIdRef.current) return;
    setLoading(false);
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setCodes(result.data as DiscountCode[]);
    } else {
      setError(result);
      setCodes([]);
    }
  }, [eventId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    load();
  }, [load]);

  return { codes, loading, isLoaded, error, reload: load };
};
